// src/components/LeadForm.js
import React, { useState, useEffect } from 'react';
import InputMask from 'react-input-mask';
import './LeadForm.css';
import { Link } from 'react-router-dom';

function LeadForm() {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    company_name: '',
    nip: '',
    phone: '',
    street: '',
    postal_code: '',
    city: '',
    email: '',
    consent_phone_calls: false,
    consent_messages: false,
    consent_marketing: false,
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [fieldErrors, setFieldErrors] = useState({
    phone: '',
    nip: '',
  });

  const validateNIP = (nip) => {
    const cleanNIP = nip.replace(/[^0-9]/g, '');
    if (cleanNIP.length !== 10) {
      return 'NIP musi składać się z 10 cyfr';
    }
    
    const weights = [6, 5, 7, 2, 3, 4, 5, 6, 7];
    let sum = 0;
    
    for (let i = 0; i < weights.length; i++) {
      sum += parseInt(cleanNIP[i]) * weights[i];
    }
    
    const checksum = sum % 11;
    if (checksum !== parseInt(cleanNIP[9])) {
      return 'Nieprawidłowy NIP';
    }
    
    return '';
  };

  const validatePhone = (phone) => {
    const cleanPhone = phone.replace(/[^0-9]/g, '');
    if (cleanPhone.length !== 9) {
      return 'Numer telefonu musi składać się z 9 cyfr';
    }
    return '';
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    if (type === 'checkbox' && name.startsWith('consent')) {
      if (name === 'consent_marketing') {
        // Jeśli zaznaczamy zgodę nadrzędną, zaznaczamy wszystkie
        if (checked) {
          setFormData(prev => ({
            ...prev,
            consent_marketing: true,
            consent_phone_calls: true,
            consent_messages: true,
          }));
        } else {
          // Jeśli odznaczamy zgodę nadrzędną, odznaczamy wszystkie
          setFormData(prev => ({
            ...prev,
            consent_marketing: false,
            consent_phone_calls: false,
            consent_messages: false,
          }));
        }
      } else {
        // Obsługa zgód podrzędnych
        const updatedFormData = {
          ...formData,
          [name]: checked,
        };

        // Sprawdzamy czy obie zgody podrzędne są odznaczone
        if (!checked && name === 'consent_phone_calls' && !formData.consent_messages) {
          updatedFormData.consent_marketing = false;
        } else if (!checked && name === 'consent_messages' && !formData.consent_phone_calls) {
          updatedFormData.consent_marketing = false;
        }

        // Jeśli zaznaczamy którąkolwiek zgodę podrzędną, zaznaczamy też nadrzędną
        if (checked) {
          updatedFormData.consent_marketing = true;
        }

        setFormData(updatedFormData);
      }
    } else {
      // Obsługa pozostałych pól formularza
      setFormData(prev => ({
        ...prev,
        [name]: newValue,
      }));
    }

    // Walidacja NIP i telefonu
    if (name === 'nip') {
      setFieldErrors(prev => ({
        ...prev,
        nip: validateNIP(value),
      }));
    } else if (name === 'phone') {
      setFieldErrors(prev => ({
        ...prev,
        phone: validatePhone(value),
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    const nipError = validateNIP(formData.nip);
    const phoneError = validatePhone(formData.phone);

    if (nipError || phoneError) {
      setFieldErrors({
        nip: nipError,
        phone: phoneError,
      });
      setErrorMessage('Popraw błędy w formularzu przed wysłaniem.');
      return;
    }

    try {
      const response = await fetch('https://api.olxbudma.pl/api/leads', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const data = await response.json();
        if (data.errors) {
          // Może zwrócić błędy walidacji - do obsłużenia wg potrzeb
          setErrorMessage('Wystąpił błąd przy zapisie. Sprawdź dane.');
        } else {
          setErrorMessage('Wystąpił błąd serwera.');
        }
      } else {
        // Sukces
        setIsSubmitted(true);
      }
    } catch (error) {
      console.error(error);
      setErrorMessage('Błąd sieci lub serwera.');
    }
  };

  // Dodajemy efekt paralaksy
  useEffect(() => {
    let ticking = false;
    let lastScrollY = window.pageYOffset;

    const handleScroll = () => {
      lastScrollY = window.pageYOffset;

      if (!ticking) {
        window.requestAnimationFrame(() => {
          const banner = document.querySelector('.banner');
          if (banner) {
            const rate = lastScrollY * 0.1; 
            banner.style.backgroundSize = `calc(100% + ${rate}%)`;
          }
          ticking = false;
        });

        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (isSubmitted) {
    return (
      <div>
        <h2>Dziękujemy za wypełnienie formularza!</h2>
        <p>Twoje dane zostały zapisane i potwierdzenie zostało wysłane na e-mail.</p>

        <div className="back-button">
          <button 
            onClick={() => {
              setIsSubmitted(false);
              setFormData({
                first_name: '',
                last_name: '',
                company_name: '',
                nip: '',
                phone: '',
                street: '',
                postal_code: '',
                city: '',
                email: '',
                consent_phone_calls: false,
                consent_messages: false,
                consent_marketing: false,
              });
              setErrorMessage('');
              setFieldErrors({ phone: '', nip: '' });
            }}
          >
            Wypełnij kolejny formularz
          </button>
        </div>

      </div>
    );
  }

  return (
    <div>
      <h2 className="welcome-title">Ty też dołącz do OLX</h2>
      <h3>
        Sprawdź, co oferujemy
      </h3>
      <p className="welcome-text">
        Wypełnij formularz kontaktowy i przekonaj się, ile możesz zyskać dzięki OLX
      </p>
      
      {errorMessage && <div style={{ color: 'red' }}>{errorMessage}</div>}

      <form onSubmit={handleSubmit}>
        <div>
          <label>Imię:</label>
          <input
            type="text"
            name="first_name"
            value={formData.first_name}
            onChange={handleChange}
            required
          />
        </div>

        <div>
          <label>Nazwisko:</label>
          <input
            type="text"
            name="last_name"
            value={formData.last_name}
            onChange={handleChange}
            required
          />
        </div>

        <div>
          <label>Nazwa firmy:</label>
          <input
            type="text"
            name="company_name"
            value={formData.company_name}
            onChange={handleChange}
          />
        </div>

        <div>
          <label>NIP:</label>
          <InputMask
            mask="999-999-99-99"
            type="text"
            name="nip"
            value={formData.nip}
            onChange={handleChange}
          />
          {fieldErrors.nip && (
            <span className="error-message">{fieldErrors.nip}</span>
          )}
        </div>

        <div>
          <label>Numer telefonu:</label>
          <InputMask
            mask="999-999-999"
            type="text"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
          />
          {fieldErrors.phone && (
            <span className="error-message">{fieldErrors.phone}</span>
          )}
        </div>
{/* 
        <div className="address-fields">
          <div>
            <label>Ulica i numer:</label>
            <input
              type="text"
              name="street"
              value={formData.street}
              onChange={handleChange}
              placeholder="np. Marszałkowska 1/5"
              
            />
          </div>

          <div>
            <label>Kod pocztowy:</label>
            <InputMask
              mask="99-999"
              type="text"
              name="postal_code"
              value={formData.postal_code}
              onChange={handleChange}
              placeholder="00-000"
              
            />
          </div>

          <div>
            <label>Miasto:</label>
            <input
              type="text"
              name="city"
              value={formData.city}
              onChange={handleChange}
              
            />
          </div>
        </div> */}

        <div>
          <label>E-mail:</label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
          />
        </div>

        <div className="consent-section">
          <h4>Zgody marketingowe</h4>
          
          <div className="consent-item">
            <label>
              <input
                type="checkbox"
                name="consent_marketing"
                checked={formData.consent_marketing}
                onChange={handleChange}
                required
              />
              Wyrażam zgodę na otrzymywanie od Grupy OLX sp. z o.o. z siedzibą w Poznaniu informacji handlowych i marketingowych m.in. o usługach, promocjach i ofertach specjalnych Grupy OLX, podmiotów powiązanych oraz partnerów biznesowych poprzez:
            </label>
          </div>

          <div className="consent-item consent-subitem">
            <label>
              <input
                type="checkbox"
                name="consent_phone_calls"
                checked={formData.consent_phone_calls}
                onChange={handleChange}
                required
              />
              połączenie telefoniczne (w tym również przez automatyczne systemy wywołujące)
            </label>
          </div>

          <div className="consent-item consent-subitem">
            <label>
              <input
                type="checkbox"
                name="consent_messages"
                checked={formData.consent_messages}
                onChange={handleChange}
                required
              />
              wiadomości tekstowe (e-mail, SMS/MMS)
            </label>
          </div>

          <p>Zgodę możesz w każdej chwili ograniczyć albo wycofać zgłaszając nam takie żądanie mailowo, na adres budma@olx.pl. <Link className="privacy-link" target="_blank" to="/polityka-prywatnosci">Pod tym linkiem możesz przeczytać politykę prywatności</Link></p>

          <p><Link className="privacy-link" target="_blank" to="https://pomoc.olx.pl/olxplhelp/s/article/Regulamin-akcji-promocyjnej-Otwórz-swój-biznes-na-OLX-odbierz-100-zwrotu-w-formie-punktów-OLX">Regulamin</Link></p>
          
        </div>

        <button type="submit">Wyślij</button>
      </form>
    </div>
  );
}

export default LeadForm;
